(ns arthur.domain.feature
  "Tracked subjects, feature ownership, and eye-pair settings.
  Features name their symbol explicitly; node ids are local to that symbol."
  (:require [arthur.domain.params :as params]))

(defn owned
  "A deterministic clip-level feature or group id. Nodes keep local names."
  [subject role]
  (keyword (subs (str subject) 1) (name role)))

(defn group-for [clip feature-id]
  (first (filter (fn [[_ group]] (some #{feature-id} (:members group)))
                 (:groups clip))))

(defn effective-params
  "Resolve static settings for one feature. A future parameter channel can
  replace a scalar at this boundary without changing feature or pair identity."
  [clip feature-id]
  (let [{:keys [subject area params] :as feature} (get-in clip [:features feature-id])
        [_ group] (group-for clip feature-id)]
    (when-not feature
      (throw (ex-info "unknown feature" {:feature feature-id})))
    (merge (params/for-area :subject)
           (get-in clip [:subjects subject :params])
           (params/for-area area)
           (:params group)
           params)))

(defn remove-from-pair
  "Keep the eye's current settings when its association is removed. Empty pairs
  are removed; a one-eye pair remains valid and can acquire a partner later."
  [clip feature-id]
  (if-let [[group-id group] (group-for clip feature-id)]
    (let [area (get-in clip [:features feature-id :area])
          values (select-keys (effective-params clip feature-id)
                              (keys (params/for-area area)))
          members (vec (remove #{feature-id} (:members group)))]
      (-> clip
          (assoc-in [:features feature-id :params] values)
          (update :groups (fn [groups]
                            (if (seq members)
                              (assoc-in groups [group-id :members] members)
                              (dissoc groups group-id))))))
    clip))

(defn problems
  "Check tracked identities and symbol-local node ownership."
  [clip]
  (let [subjects (:subjects clip)
        features (:features clip)
        groups (:groups clip)
        memberships (mapcat (comp :members val) groups)
        node-owners (for [[_ f] features n (:nodes f)]
                      [(:symbol f) n])]
    (vec
     (concat
      (for [[id s] subjects :when (not= id (:id s))]
        (str "subject " (pr-str id) " has a different :id"))
      (for [[id s] subjects
            :when (not (params/valid-settings? :subject (or (:params s) {})))]
        (str "subject " (pr-str id) " has invalid settings"))
      (for [[id _] subjects
            :when (not (seq (get-in clip [:symbols id :nodes :head :measured])))]
        (str "subject " (pr-str id) " has no measured head in its symbol"))
      (for [[id f] features :when (not= id (:id f))]
        (str "feature " (pr-str id) " has a different :id"))
      (for [[id f] features :when (not (contains? subjects (:subject f)))]
        (str "feature " (pr-str id) " has no subject"))
      (for [[id f] features :when (not (contains? (disj params/areas :subject) (:area f)))]
        (str "feature " (pr-str id) " has an unknown area"))
      (for [[id f] features
            :when (not (params/valid-settings? (:area f) (or (:params f) {})))]
        (str "feature " (pr-str id) " has invalid settings for " (pr-str (:area f))))
      (for [[id f] features
            :when (not (contains? (:symbols clip) (:symbol f)))]
        (str "feature " (pr-str id) " names a missing symbol"))
      (for [[id f] features node-id (:nodes f)
            :let [owned-nodes (get-in clip [:symbols (:symbol f) :nodes])]
            :when (not (contains? owned-nodes node-id))]
        (str "feature " (pr-str id) " refers to missing node " (pr-str node-id)))
      (for [[id n] (frequencies node-owners) :when (> n 1)]
        (str "node " (pr-str id) " belongs to more than one feature"))
      (for [[id g] groups :when (not= id (:id g))]
        (str "group " (pr-str id) " has a different :id"))
      (for [[id g] groups
            :when (not (and (= :eye-pair (:kind g))
                            (<= 1 (count (:members g)) 2)
                            (= (count (:members g)) (count (distinct (:members g))))))]
        (str "group " (pr-str id) " must be an eye pair of one or two distinct eyes"))
      (for [[id g] groups
            :when (not (params/valid-settings? :eye (or (:params g) {})))]
        (str "group " (pr-str id) " has invalid eye settings"))
      (for [[id g] groups member (:members g)
            :let [f (get features member)]
            :when (not (and f (= :eye (:area f)) (= (:subject g) (:subject f))))]
        (str "group " (pr-str id) " has an eye from another subject or an unknown feature"))
      (for [[id n] (frequencies memberships) :when (> n 1)]
        (str "feature " (pr-str id) " belongs to more than one group"))))))
